import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Flex } from "@mantine/core";
import {
  SelectedService,
  ServiceSchedule,
} from "../../../../../../types/availability";
import {
  CalendarEventType,
  SlotInfo as CalendarSlotInfo,
} from "../../../../../../types/calendar";
import { useCalendar } from "../../../../../../context/CalendarContext";
import { useCalendarEvent } from "../../../../../../context/CalendarEventContext";
import { CalendarDropInfo } from "../../Views/DnDCalendar";
import {
  snapToIncrement,
  SNAP_INCREMENT_MINS,
} from "../../../calendarDragHelpers";
import {
  buildTransientEvents,
  wallClockParts,
} from "./schedulingCalendarUtils";
import ServiceScheduleList from "./ServiceScheduleList";
import SchedulingCalendar from "./SchedulingCalendar";
import styles from "./ScheduleServicesStep.module.css";

/** Minimal client shape needed for the transient event label. */
interface StepClient {
  id?: string;
  first_name?: string;
  last_name?: string;
}

/** Minimal salon-service shape needed to resolve calendar colors. */
interface StepSalonService {
  id: string;
  default_calendar_event_color?: string;
}

interface ScheduleServicesStepProps {
  selectedServices: SelectedService[];
  serviceSchedules: Record<string, ServiceSchedule>;
  onScheduleService: (serviceId: string, date: string, time: string) => void;
  onClearSchedule: (serviceId: string) => void;
  salonServices?: StepSalonService[];
  client: StepClient | null;
}

export default function ScheduleServicesStep({
  selectedServices,
  serviceSchedules,
  onScheduleService,
  onClearSchedule,
  salonServices,
  client,
}: ScheduleServicesStepProps) {
  const { setSelectedArtists } = useCalendar();
  const { events: calendarEvents } = useCalendarEvent() as {
    events: CalendarEventType[];
  };

  // Which service the next slot-click will schedule.
  const [activeServiceId, setActiveServiceId] = useState<string | null>(null);

  // Keep an active service selected: default to the first unscheduled one (or
  // the first service) whenever the current selection is missing/invalid.
  useEffect(() => {
    if (selectedServices.length === 0) {
      setActiveServiceId(null);
      return;
    }
    const stillValid =
      activeServiceId && selectedServices.some((s) => s.id === activeServiceId);
    if (!stillValid) {
      const firstUnscheduled = selectedServices.find(
        (s) => !serviceSchedules[s.id],
      );
      setActiveServiceId((firstUnscheduled ?? selectedServices[0]).id);
    }
  }, [selectedServices, activeServiceId, serviceSchedules]);

  // Staff of the active service — the calendar is focused on this person.
  const activeStaffId = useMemo(
    () =>
      selectedServices.find((s) => s.id === activeServiceId)?.staffId ?? null,
    [selectedServices, activeServiceId],
  );

  // Default the calendar's staff filter to the active service's staff, so
  // DayView shows just that person's column and existing appointments. Restored
  // by RebookModal's snapshot on close.
  useEffect(() => {
    if (activeStaffId) {
      setSelectedArtists([activeStaffId]);
    }
  }, [activeStaffId, setSelectedArtists]);

  // Proposed (dashed-outline) events for services that already have a time.
  const transientEvents = useMemo(
    () =>
      buildTransientEvents(
        selectedServices,
        serviceSchedules,
        activeServiceId,
        salonServices,
        client,
      ),
    [
      selectedServices,
      serviceSchedules,
      activeServiceId,
      salonServices,
      client,
    ],
  );

  // Focus the calendar on the active service's staff: keep only that staff's
  // real appointments + transient events. DayView also filters by resource
  // column, but this keeps Week view (no columns) consistent.
  const mergedEvents = useMemo(() => {
    const all = [...calendarEvents, ...transientEvents];
    if (!activeStaffId) return all;
    return all.filter(
      (e) =>
        e.resourceId === activeStaffId ||
        (e as { secondary_staff_id?: string }).secondary_staff_id ===
          activeStaffId,
    );
  }, [calendarEvents, transientEvents, activeStaffId]);

  // Click an empty slot -> schedule the active service at that date/time. The
  // slot's browser-local wall-clock already equals the salon wall-clock (see
  // getSalonNow / mapBookedServiceToEvent), read via wallClockParts.
  const handleSelectSlot = useCallback(
    (slot: CalendarSlotInfo) => {
      if (!activeServiceId) return;
      const { date, time } = wallClockParts(slot.start);
      onScheduleService(activeServiceId, date, time);

      // Gently advance to the next still-unscheduled service so repeated clicks
      // fill the list; the user can re-select any card to override.
      const next = selectedServices.find(
        (s) => s.id !== activeServiceId && !serviceSchedules[s.id],
      );
      if (next) {
        setActiveServiceId(next.id);
      }
    },
    [activeServiceId, onScheduleService, selectedServices, serviceSchedules],
  );

  // Drag a proposed event to a new slot -> reschedule that service. Only
  // transient events are draggable (see SchedulingCalendar's accessors); snap to
  // the same 15-min grid the main calendar uses. Duration is fixed (derived),
  // so we only need the new start.
  const handleEventDrop = useCallback(
    (info: CalendarDropInfo) => {
      if (!(info.event as { isTransient?: boolean }).isTransient) return;
      const serviceId = info.event.id.replace(/^transient-/, "");
      const snappedStart = snapToIncrement(info.start, SNAP_INCREMENT_MINS);
      const { date, time } = wallClockParts(snappedStart);
      onScheduleService(serviceId, date, time);
      setActiveServiceId(serviceId);
    },
    [onScheduleService],
  );

  // Click a proposed event -> re-select that service (ids are `transient-<id>`).
  const handleSelectEvent = useCallback((event: CalendarEventType) => {
    if ((event as { isTransient?: boolean }).isTransient) {
      setActiveServiceId(event.id.replace(/^transient-/, ""));
    }
  }, []);

  return (
    <Flex
      direction={{ base: "column", md: "row" }}
      gap="md"
      className={styles.splitRoot}
    >
      <Box className={styles.listPane}>
        <ServiceScheduleList
          services={selectedServices}
          schedules={serviceSchedules}
          activeServiceId={activeServiceId}
          onSelectActive={setActiveServiceId}
          onClearSchedule={onClearSchedule}
        />
      </Box>

      <Box className={styles.calendarPane}>
        <SchedulingCalendar
          events={mergedEvents}
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          onEventDrop={handleEventDrop}
        />
      </Box>
    </Flex>
  );
}
